/**
 * StepUpProvider - global step-up authentication context.
 *
 * Sensitive actions (change password, add bank, delete account, disable 2FA)
 * MUST require a fresh biometric or password proof-of-presence at most every
 * REAUTH.STEP_UP_FRESH_MS (5 min).
 *
 * Usage:
 *   const { requireStepUp } = useStepUp();
 *   const ok = await requireStepUp({ reason: "Change password" });
 *   if (!ok) return; // user cancelled
 *   // ...proceed with the sensitive action
 *
 * Flow:
 *  1. If a fresh step-up exists (< 5 min) - resolve true silently.
 *  2. If the user has a passkey - prompt biometric via loginWithPasskey().
 *  3. Otherwise - show a password modal that verifies via
 *     POST /api/security/step-up/verify-password.
 *
 * On any success, markStepUpNow() flags the timestamp so subsequent
 * sensitive actions within 5 min are silent.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScanFace, KeyRound, Loader2, X, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { API } from '../../App';
import {
  loginWithPasskey,
  hasPasskey as apiHasPasskey,
  platformAuthenticatorAvailable,
  hasFreshStepUp,
  markStepUpNow,
  markUnlockedNow,
} from '../../utils/webauthn';
import { isStandalone } from '../../utils/pwa';
import {
  isNativeApp,
  getBiometryInfo,
  nativeBiometricAuth,
  nativeHaptic,
} from '../../utils/nativeBridge';

const StepUpContext = createContext(null);
export const useStepUp = () => useContext(StepUpContext) || { requireStepUp: async () => true };

const readUserEmail = () => {
  try { return JSON.parse(localStorage.getItem('akountfly_user') || 'null')?.email || ''; } catch { return ''; }
};

export function StepUpProvider({ children }) {
  const [state, setState] = useState({ open: false, reason: '', mode: 'auto' });
  const resolverRef = useRef(null);

  const requireStepUp = useCallback(async ({ reason = 'Confirm your identity', force = false, passwordOnly = false } = {}) => {
    if (!force && hasFreshStepUp()) return true;
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, reason, mode: 'auto', passwordOnly });
    });
  }, []);

  const close = useCallback((result) => {
    setState((s) => ({ ...s, open: false }));
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(result);
  }, []);

  const ctx = useMemo(() => ({ requireStepUp }), [requireStepUp]);

  return (
    <StepUpContext.Provider value={ctx}>
      {children}
      <StepUpModal state={state} onClose={close} />
    </StepUpContext.Provider>
  );
}

/* ------------------------------ modal ------------------------------ */

function StepUpModal({ state, onClose }) {
  const [phase, setPhase] = useState('choosing'); // choosing | biometric | password | success
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [canBiometric, setCanBiometric] = useState(false);
  const emailRef = useRef('');

  // Contextual biometric label - "Try Face ID again" / "Try Fingerprint again"
  // adapts to the actual platform so the copy stays honest across
  // iOS / Android / Windows / Mac.
  const bioLabel = (() => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    // iPhone Duo (2026) ships Touch ID; the web UA cannot tell models apart.
    if (/iPhone/i.test(ua)) return 'Face ID / Touch ID';
    if (/iPad|Mac/i.test(ua)) return 'Touch ID';
    if (/Android/i.test(ua)) return 'Fingerprint';
    if (/Windows/i.test(ua)) return 'Windows Hello';
    return 'biometric';
  })();

  useEffect(() => {
    if (!state.open) { setPhase('choosing'); setError(''); setPassword(''); setBusy(false); return; }
    emailRef.current = readUserEmail();
    (async () => {
      try {
        // Caller (typically BackgroundLockHost after a biometric failure)
        // can force password-only mode. Prevents cascading into another
        // failing WebAuthn prompt.
        if (state.passwordOnly) {
          setCanBiometric(false);
          setPhase('password');
          return;
        }
        // Native wrapper: skip WebAuthn entirely and probe OS biometry via
        // the bridge. Android WebView has no WebAuthn support, and iOS
        // WKWebView needs an Associated-Domains entitlement we don't ship.
        if (isNativeApp()) {
          const info = await getBiometryInfo();
          const has = !!(info.available && info.enrolled);
          setCanBiometric(has);
          if (has) {
            setPhase('biometric');
            runBiometric();
          } else {
            setPhase('password');
          }
          return;
        }

        // Biometric step-up is PWA-only. On regular web (PC or mobile web)
        // we always fall back to the password modal even if the browser has
        // a passkey registered - a Face ID prompt inside a browser tab
        // feels foreign, and passkeys stored against a browser can be
        // wiped by ordinary cache/cookie clears without the user noticing.
        if (!isStandalone()) {
          setCanBiometric(false);
          setPhase('password');
          return;
        }
        const available = await platformAuthenticatorAvailable();
        const has = available && emailRef.current ? await apiHasPasskey(emailRef.current) : false;
        setCanBiometric(!!has);
        // If biometric is available, auto-invoke it - matches Apple's step-up pattern.
        if (has) {
          setPhase('biometric');
          runBiometric();
        } else {
          setPhase('password');
        }
      } catch {
        setPhase('password');
      }
    })();
    // Auto-run biometric when the modal opens if available.
  }, [state.open]);

  const runBiometric = async () => {
    setError('');
    setBusy(true);
    try {
      // Native path: trust the OS biometric prompt as the step-up gate.
      // Per the mobile team's contract, we don't need a server verification
      // token here - the native call is an on-device OS-verified check.
      if (isNativeApp()) {
        const res = await nativeBiometricAuth(state.reason || 'Verify to continue');
        if (res.success) {
          nativeHaptic('success');
          markStepUpNow();
          markUnlockedNow();
          setPhase('success');
          setTimeout(() => onClose(true), 350);
        } else if (res.error === 'user_cancel') {
          setPhase('password');
        } else {
          setError('Biometric check failed. Enter your password to continue.');
          setPhase('password');
        }
        return;
      }

      await loginWithPasskey(emailRef.current);
      markStepUpNow();
      markUnlockedNow();
      setPhase('success');
      setTimeout(() => onClose(true), 350);
    } catch (e) {
      const name = String(e?.name || '');
      const msg = String(e?.message || '').toLowerCase();
      // NotAllowedError covers user cancel, timeout, and RP-ID / credential
      // mismatch (browser hides which one for privacy). All of these
      // should silently drop to password without a scary error banner.
      const isNotAllowed =
        name === 'NotAllowedError' ||
        msg.includes('not allowed') ||
        msg.includes('notallowed') ||
        msg.includes('cancel') ||
        msg.includes('abort');
      // Server-side reject (backend 401 with error-code detail) after a
      // successful biometric on device. Face ID hardware is fine.
      const isServerReject =
        msg.includes('passkey_verify_failed') ||
        msg.includes('passkey_not_recognized') ||
        msg.includes('passkey_wrong_account') ||
        msg.includes('challenge expired') ||
        msg.includes('could not start');
      try { console.warn('[akf.biometric] step-up failed', { name, msg }); } catch (_) { /* noop */ }
      if (isNotAllowed || isServerReject) {
        setPhase('password');
      } else {
        setError(e?.message || 'Biometric check failed. Enter your password to continue.');
        setPhase('password');
      }
    } finally {
      setBusy(false);
    }
  };

  const runPassword = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!password) { setError('Please enter your password.'); return; }
    setError('');
    setBusy(true);
    try {
      await axios.post(`${API}/security/step-up/verify-password`, { password });
      markStepUpNow();
      setPhase('success');
      setTimeout(() => onClose(true), 300);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Incorrect password.');
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {state.open && (
        <motion.div
          className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-sm p-3"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="step-up-modal"
        >
          <motion.div
            className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.35)]"
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 340, damping: 30 }}
          >
            <div className="relative pt-7 pb-5 px-6 bg-gradient-to-br from-[#283F8B] via-[#1e3170] to-[#131f4d] text-white text-center">
              <button
                onClick={() => onClose(false)}
                aria-label="Cancel"
                data-testid="step-up-close-btn"
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center active:scale-90 transition-all"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-white/12 border border-white/20 backdrop-blur-lg flex items-center justify-center mb-3 relative">
                {phase === 'success' ? <ShieldCheck className="w-8 h-8 text-white" /> :
                 phase === 'biometric' ? <ScanFace className="w-8 h-8 text-white" strokeWidth={1.7} /> :
                 <KeyRound className="w-8 h-8 text-white" strokeWidth={1.7} />}
                {phase === 'biometric' && (
                  <motion.span className="absolute inset-0 rounded-2xl bg-[#5FC6C5]/40"
                    animate={{ scale: [1, 1.25], opacity: [0.5, 0] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut' }}
                  />
                )}
              </div>
              <h2 className="text-xl font-extrabold tracking-tight">
                {phase === 'success' ? 'Confirmed' : 'Confirm it\u2019s you'}
              </h2>
              <p className="text-white/75 text-sm mt-1">{state.reason}</p>
            </div>

            <div className="px-6 pt-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              {phase === 'biometric' && (
                <div className="text-center py-6" data-testid="step-up-biometric-phase">
                  <p className="text-sm text-slate-600 mb-4">
                    {bioLabel === 'Fingerprint' || bioLabel === 'Touch ID'
                      ? `Touch the sensor to confirm with ${bioLabel}.`
                      : `Look at your device to confirm with ${bioLabel}.`}
                  </p>
                  <button
                    onClick={runBiometric}
                    disabled={busy}
                    className="w-full h-12 rounded-xl bg-gradient-to-r from-[#283F8B] to-[#108981] text-white font-bold shadow-[0_10px_25px_rgba(40,63,139,0.25)] active:scale-[0.98] transition-transform disabled:opacity-60"
                    data-testid="step-up-biometric-retry-btn"
                  >
                    {busy ? (<><Loader2 className="w-4 h-4 mr-1 inline animate-spin" /> Waiting...</>) : `Try ${bioLabel} again`}
                  </button>
                  <button
                    onClick={() => setPhase('password')}
                    className="w-full h-11 mt-2 text-slate-500 text-sm font-semibold active:scale-[0.98] transition-transform"
                    data-testid="step-up-use-password-btn"
                  >
                    Use password instead
                  </button>
                </div>
              )}

              {phase === 'password' && (
                <form onSubmit={runPassword} data-testid="step-up-password-phase" className="space-y-3">
                  <input
                    type="password"
                    autoFocus
                    placeholder="Your password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={busy}
                    data-testid="step-up-password-input"
                    className="w-full h-12 rounded-xl border border-slate-200 px-4 text-[15px] focus:border-[#283F8B] focus:outline-none focus:ring-4 focus:ring-[#283F8B]/15 transition-all"
                  />
                  {error && <p className="text-sm text-red-600" data-testid="step-up-error">{error}</p>}
                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full h-12 rounded-xl bg-gradient-to-r from-[#283F8B] to-[#108981] text-white font-bold shadow-[0_10px_25px_rgba(40,63,139,0.25)] active:scale-[0.98] transition-transform disabled:opacity-60"
                    data-testid="step-up-password-submit-btn"
                  >
                    {busy ? (<><Loader2 className="w-4 h-4 mr-1 inline animate-spin" /> Verifying...</>) : 'Confirm'}
                  </button>
                  {canBiometric && (
                    <button
                      type="button"
                      onClick={() => { setPhase('biometric'); runBiometric(); }}
                      className="w-full h-11 text-slate-500 text-sm font-semibold active:scale-[0.98] transition-transform"
                      data-testid="step-up-use-biometric-btn"
                    >
                      Use Face ID instead
                    </button>
                  )}
                </form>
              )}

              {phase === 'success' && (
                <div className="text-center py-6" data-testid="step-up-success-phase">
                  <p className="text-slate-600 text-sm">You&apos;re verified. Continuing...</p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
